import React, { useState, useEffect } from 'react';
import { pwaInstallHandler } from '../../../utils/serviceWorker';
import styles from './PWAInstallPrompt.module.css';

interface PWAInstallPromptProps {
  onInstall?: () => void;
  onDismiss?: () => void;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ onInstall, onDismiss }) => {
  const [canInstall, setCanInstall] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if install prompt was previously dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Check if app is already installed
    if (pwaInstallHandler.isAppInstalled()) {
      return;
    }

    // Check if install prompt is available
    const checkInstallability = () => {
      const canInstallApp = pwaInstallHandler.canInstall();
      setCanInstall(canInstallApp);
      
      if (canInstallApp && !isDismissed) {
        // Show prompt after a delay to avoid being intrusive
        setTimeout(() => {
          setIsVisible(true);
        }, 5000);
      }
    };

    checkInstallability();

    // Listen for beforeinstallprompt event changes
    const handleBeforeInstallPrompt = () => {
      checkInstallability();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isDismissed]);

  const handleInstall = async () => {
    try {
      const installed = await pwaInstallHandler.promptInstall();
      if (installed) {
        setIsVisible(false);
        if (onInstall) {
          onInstall();
        }
      }
    } catch (error) {
      console.error('Failed to install PWA:', error);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleRemindLater = () => {
    setIsVisible(false);
    // Show again after 24 hours
    setTimeout(() => {
      if (pwaInstallHandler.canInstall() && !pwaInstallHandler.isAppInstalled()) {
        setIsVisible(true);
      }
    }, 24 * 60 * 60 * 1000);
  };

  if (!canInstall || !isVisible || isDismissed) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.prompt}>
        <div className={styles.header}>
          <div className={styles.icon}>📱</div>
          <div className={styles.headerText}>
            <h3 className={styles.title}>Install Chess Codebreaker</h3>
            <p className={styles.subtitle}>Access your spy training anywhere</p>
          </div>
          <button 
            className={styles.closeButton}
            onClick={handleDismiss}
            aria-label="Close install prompt"
          >
            ✕
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.benefits}>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>🚀</span>
              <span className={styles.benefitText}>Faster loading</span>
            </div>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>📴</span>
              <span className={styles.benefitText}>Works offline</span>
            </div>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>🏠</span>
              <span className={styles.benefitText}>Add to home screen</span>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.remindButton}
            onClick={handleRemindLater}
          >
            Remind Later
          </button>
          <button 
            className={styles.installButton}
            onClick={handleInstall}
          >
            Install App
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;